import { createSelector } from '@reduxjs/toolkit';
import type { FunctionComponent, TargetedEvent } from 'preact';
import { useEffect, useMemo, useRef, useState } from 'preact/hooks';

import { vehicleLandingFacetField } from 'Addons/fitmentSearch/vehicleLandingFacetField';
import VehicleWidget from 'Components/fitmentSearch/vehicleWidget/index';
import type { VehicleWidgetTemplateParams } from 'Components/fitmentSearch/vehicleWidget/vehicleWidget';
import { FacetTilesVehicleWidgetName } from 'Components/names';
import FilterInput from 'Components/smallComponents/filterInput';
import type {
  InputTemplateInvoker,
  RepeaterFunctionInvoker,
  TemplateFunction,
  TemplateFunctionInvoker,
  TemplateResult,
} from 'Components/types';
import { toggle } from 'Core/actions/request';
import useScroll, { type UseScroll } from 'Core/hooks/horizontalScroll';
import { prepareResponseFacets } from 'Core/reducers/search/response';
import {
  createSelectedVehicleSelectionSelector,
  fitmentSearchFieldsSelector,
} from 'Core/selectors/fitmentSearch/index';
import { createFacetSelector, searchRequestSelectionSelector } from 'Core/selectors/search';
import { useDispatch, useSelector } from 'Core/store';
import type { Facet, FacetValueFull, SelectionEntry } from 'Models/index';
import fitmentSearchConfig from 'Models/uiConfig/fitmentSearchConfig';
import requestConfig from 'Models/uiConfig/requestConfig';
import { getRequestFromUri, getUriFromRequest, getVehicleFromUri } from 'Modules/converter/index';
import { abortControllerCreator, facets } from 'Modules/serverApi/index';
import { DisplayMode, type RequestSelectionItem } from 'Modules/serverApi/types';
import { cloneSafe } from 'Utils/components';

import Subtile from './subtile';
import Tile, { type Params as TileParams, type SubvalueParams } from './tile';

const uriRequest = (getRequestFromUri(window.location).selection || []).filter(
  (selection): selection is SelectionEntry => typeof selection.term === 'string',
);

interface LetterTileParams extends LetterParams {
  tiles: RepeaterFunctionInvoker<TileParams>;
}
interface FilterLetterParams extends LetterParams {
  currentLetter: string;
  isDisabled: boolean;
  setCurrentLetter: (letter: string) => void;
}
interface LetterParams {
  letter: string;
}

type Params = {
  alphabeticalGroups: RepeaterFunctionInvoker<LetterParams>;
  alphabeticalLetters: RepeaterFunctionInvoker<LetterParams>;
  currentLetter: string;
  facetField: string;
  hierarchicalGroups: RepeaterFunctionInvoker<TileParams>;
  vehicleWidget: TemplateFunctionInvoker<VehicleWidgetTemplateParams>;
  isMultiLevel?: boolean;
  view?: string;
  filterInput: InputTemplateInvoker;
} & UseScroll;

type Props = {
  template: TemplateFunction<Params>;
  columnBreakpoint?: number;
  excludeFieldPreselection?: string;
  facetField?: string;
  initCollapsed?: boolean;
  isAlwaysActive?: boolean;
  isMultiLevel?: boolean;
  isVehicleWidgetDisabled?: boolean;
  useFilterMode?: boolean;
  view?: string;
  useNativeDropdown?: boolean;
  useSearchableDropdown?: boolean;
};

const FacetTiles: FunctionComponent<Props> = ({
  template,
  columnBreakpoint,
  excludeFieldPreselection,
  facetField = fitmentSearchConfig.currentCategorySelectionPageField as string,
  initCollapsed,
  isAlwaysActive,
  isMultiLevel,
  isVehicleWidgetDisabled,
  useFilterMode = false,
  view,
  useNativeDropdown,
  useSearchableDropdown,
}) => {
  const dispatch = useDispatch();

  const rootRef = useRef<HTMLElement>(null);

  const requestController = useMemo(() => abortControllerCreator(), []);

  const [filterValue, setFilterValue] = useState('');

  const [tilesFacet, setTilesFacet] = useState<Facet>();
  const [vehicleUriSelection, setVehicleUriSelection] = useState<SelectionEntry[]>([]);
  const [searchInitialSelection, setSearchInitialSelection] = useState<SelectionEntry[]>(uriRequest);

  const { scrollLeft, scrollRight, isLeftBorder, isRightBorder } = useScroll(rootRef);

  const selectedVehicleSelection = useSelector(createSelectedVehicleSelectionSelector);
  const fitmentFields = useSelector(fitmentSearchFieldsSelector);
  const vehicleFacetSelection =
    useSelector(
      createSelector(searchRequestSelectionSelector, (selection) =>
        selection.find((s) => s.field === vehicleLandingFacetField),
      ),
    ) ?? searchInitialSelection.find((s) => s.field === vehicleLandingFacetField);

  const filteredTiles = tilesFacet?.getFilteredValues(filterValue);

  const [currentLetter, setCurrentLetter] = useState<string>('All');
  const alphabeticalGroups = useGroupAlphabetically(filteredTiles);

  const [currentTab, setCurrentTab] = useState<string | null>(null);
  const hierarchicalGroups = useGroupHierarchically(filteredTiles);

  const responseFacet = useSelector(createFacetSelector(facetField));

  useEffect(() => {
    if (!currentTab && hierarchicalGroups.length) {
      setCurrentTab(hierarchicalGroups[0][0].value);
    }
  }, [currentTab, hierarchicalGroups]);

  useEffect(() => {
    if (useFilterMode) {
      setTilesFacet(responseFacet);
    }
  }, [responseFacet, useFilterMode]);

  useEffect(() => {
    if (useFilterMode) {
      return;
    }

    const { isFitmentSearchWidgetsVisible, hideVehicleFromUrl, baseFields } = fitmentSearchConfig;
    const vehicleSelectionFromUri: SelectionEntry[] = getVehicleFromUri().selection;

    const isPartialVehicleInUrl =
      vehicleSelectionFromUri.length > 0 && vehicleSelectionFromUri.length < baseFields.length;

    const isVehicleWidgetAvailable = isFitmentSearchWidgetsVisible || !isVehicleWidgetDisabled;
    const isVehicleSelected = !isPartialVehicleInUrl || selectedVehicleSelection.length >= baseFields.length;

    const vehicleSelection: SelectionEntry[] = [
      ...(isVehicleWidgetAvailable && isVehicleSelected
        ? selectedVehicleSelection
        : vehicleSelectionFromUri.length
          ? vehicleSelectionFromUri
          : []),
    ];

    if (vehicleFacetSelection?.term) {
      vehicleSelection.push({ field: vehicleFacetSelection.field, term: vehicleFacetSelection.term });
    }

    const preselection = [
      ...vehicleSelection,
      ...requestConfig.localPreselection,
      ...requestConfig.defaultSelection,
    ]
      .filter((v) => v.field !== excludeFieldPreselection)
      .map((v) => {
        return { ...v, hidden: v.field !== facetField };
      });

    const facetSelection: RequestSelectionItem = { field: facetField, expand: isMultiLevel };

    const extra = excludeFieldPreselection ? { excludeFieldPreselection } : {};
    const request = {
      selection: [...preselection, facetSelection],
      extra,
    };

    const signal = requestController.create();
    facets(request, signal).then((response) => {
      if (signal.aborted) {
        return;
      }

      const precessedResponse = prepareResponseFacets(
        requestConfig.facetResponseHandler(response).Facets as Parameters<typeof prepareResponseFacets>[0],
        [],
      );

      const tilesFacet = precessedResponse?.get(facetField);

      if (tilesFacet?.values?.length || requestConfig.hasSearchResults) {
        setTilesFacet(tilesFacet);
      } else if (!requestConfig.hasHtmxResults) {
        window.location.assign(getUriFromRequest({ selection: vehicleSelection }, { goToSearchPage: true }));
      }

      setSearchInitialSelection([]);
    });

    if (!hideVehicleFromUrl || vehicleSelectionFromUri.length) {
      selectedVehicleSelection.length === 0 && isPartialVehicleInUrl
        ? setVehicleUriSelection(vehicleSelectionFromUri)
        : setVehicleUriSelection(selectedVehicleSelection);
    }

    return () => requestController.abort(signal);
  }, [
    dispatch,
    requestController,
    excludeFieldPreselection,
    facetField,
    isMultiLevel,
    isVehicleWidgetDisabled,
    useFilterMode,
    selectedVehicleSelection,
    vehicleFacetSelection,
  ]);

  const vehicleWidget: TemplateFunctionInvoker<VehicleWidgetTemplateParams> = (templ) => {
    const props = {
      isAlwaysActive: isAlwaysActive ?? false,
      initCollapsed,
      isHidden: isVehicleWidgetDisabled,
      columnBreakpoint,
      doNotRedirectOnVehicleSelect: true,
      name: FacetTilesVehicleWidgetName,
      useNativeDropdown,
      useSearchableDropdown,
      template: templ,
    };
    return (<VehicleWidget {...props} key="VehicleWidget" />) as TemplateResult;
  };

  const toggleFacet = (term: string) => {
    dispatch(
      toggle(
        { field: facetField, term },
        { isSingleValue: responseFacet?.displayMode === DisplayMode.Radio, mayDiscardValue: true },
      ),
    );
  };

  const alphabeticalLetters = alphabeticalGroups.map(
    ([letter, values]) =>
      (templ) =>
        cloneSafe(
          templ.call({
            currentLetter,
            isDisabled: letter !== 'All' && !values.length,
            letter,
            setCurrentLetter,
          }),
          null,
          { key: `filterLetter-${letter}` },
        ),
  ) as RepeaterFunctionInvoker<FilterLetterParams>;

  const filteredAlphabeticalGroup = useFilterGroup(currentLetter, alphabeticalGroups, ([letter]) => letter);
  const filteredHierarchicalGroups = useFilterGroup(
    currentLetter,
    hierarchicalGroups,
    ([facetValue]) => facetValue.term,
  );

  const alphabeticalGroupsRepeater = filteredAlphabeticalGroup.map(([letter, values]) => (templ) => {
    const tilesRepeater = values.map((value) => (templ) => {
      const props = {
        template: templ,
        subvalue: value,
        toggleFacet,
        useFilterMode,
        vehicleFromUriSelection: vehicleUriSelection,
        fitmentFields,
      };
      return <Subtile {...props} key={value.term} />;
    }) as RepeaterFunctionInvoker<SubvalueParams>;

    const component = templ.call({ letter, tiles: tilesRepeater });
    return cloneSafe(component, null, { key: letter });
  }) as RepeaterFunctionInvoker<LetterTileParams>;

  const hierarchicalGroupsRepeater = filteredHierarchicalGroups.map(([value, subvalues]) => (templ) => {
    const props = {
      template: templ,
      value,
      subvalues,
      currentTab,
      isMultiLevel,
      toggleFacet,
      useFilterMode,
      vehicleFromUriSelection: vehicleUriSelection,
      fitmentFields,
      setCurrentTab,
      vehicleWidget,
    };
    return <Tile {...props} key={value.term} />;
  }) as RepeaterFunctionInvoker<TileParams>;

  const filterInput: InputTemplateInvoker = (templ) => {
    if (!tilesFacet) {
      return null;
    }

    const props = {
      template: templ,
      filterValue,
      facetName: tilesFacet.name,
      facetField: tilesFacet.field,
      onChange: (e: TargetedEvent<HTMLInputElement, Event>) => setFilterValue(e.currentTarget.value),
    };
    return (<FilterInput {...props} key={tilesFacet.field} />) as ReturnType<InputTemplateInvoker>;
  };

  const component = template.call({
    alphabeticalGroups: alphabeticalGroupsRepeater,
    alphabeticalLetters,
    currentLetter,
    hierarchicalGroups: hierarchicalGroupsRepeater,
    isMultiLevel,
    facetField,
    view,
    vehicleWidget,
    scrollLeft,
    scrollRight,
    isLeftBorder,
    isRightBorder,
    filterInput,
  });

  return cloneSafe(component, rootRef);
};

const charArray = ['All', '#', ...'ABCDEFGHIJKLMNOPQRSTUVWXYZ'];

function getFilterChar(str: string) {
  const firstChar = str.charAt(0).toUpperCase();
  return charArray.includes(firstChar) ? firstChar : '#';
}

function useFilterGroup<T>(currentLetter: string, group: T[], keySelector: (value: T) => string): T[] {
  return currentLetter !== 'All'
    ? group.filter((value) => getFilterChar(keySelector(value)) === currentLetter)
    : group;
}

type AlphabeticalFacet = [string, FacetValueFull[]][];
function useGroupAlphabetically(values: FacetValueFull[] | undefined): AlphabeticalFacet {
  if (!values?.length) {
    return [];
  }

  return values.reduce(
    (groups, facetValue) => {
      const keyChar = getFilterChar(facetValue.term);
      const groupIndex = groups.map((group) => group[0]).indexOf(keyChar);

      if (groupIndex > 0) {
        (groups[groupIndex][1] as FacetValueFull[]).push(facetValue);
      }

      return [...groups];
    },
    charArray.map((char) => [char, []]),
  ) as AlphabeticalFacet;
}

type FacetSubvaluesPairs = [FacetValueFull, FacetValueFull[]][];
function useGroupHierarchically(values: FacetValueFull[] | undefined): FacetSubvaluesPairs {
  if (!values?.length) {
    return [];
  }

  return values.reduce<FacetSubvaluesPairs>((pairs, facetValue) => {
    switch (facetValue.treeLevel) {
      // not Tree facet or first level value
      case null:
      case 0:
        pairs.push([facetValue, []]);
        break;
      // subvalue
      default: {
        const value = pairs[pairs.length - 1];

        // there are subvalues but no parent value
        if (value && !value[0].term.includes('>')) {
          // so far only the second level is supported
          if (facetValue.treeLevel === 1) {
            pairs[pairs.length - 1][1].push(facetValue);
          }
        } else {
          pairs.push([facetValue, []]);
        }
        break;
      }
    }
    return pairs;
  }, []);
}

export default FacetTiles;
